import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, Send } from "lucide-react";

const EMPTY_FORM = { api_key: "", sender_email: "", sender_name: "Joanna Beck Ministries", reply_to: "" };

export const EmailSendingCard = () => {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/settings/email-status").then((r) => {
      setStatus(r.data);
      setForm((f) => ({
        ...f,
        sender_email: r.data.source === "settings" ? r.data.sender : f.sender_email,
        sender_name: r.data.sender_name || f.sender_name,
        reply_to: r.data.reply_to || "",
      }));
    }).catch(() => {});
  };

  useEffect(load, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/email", { ...form, app_origin: window.location.origin });
      setStatus(data);
      setForm((f) => ({ ...f, api_key: "" }));
      toast.success("Email settings saved. Click \"Send me a test email\" to confirm it works.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/settings/email/test");
      toast.success(`Test email sent to ${data.to}. Check your inbox (and spam folder).`);
    } catch (e) {
      toast.error(apiError(e), { duration: 10000 });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete("/settings/email");
      setStatus(data);
      setForm(EMPTY_FORM);
      toast.success("Resend key removed. Emails are back in preview mode.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const connected = status?.enabled;
  const fromSettings = status?.source === "settings";

  return (
    <Card className="shadow-sm" data-testid="email-settings-card">
      <CardHeader>
        <CardTitle className="font-serif text-2xl flex items-center gap-2"><Mail className="h-5 w-5 text-primary" /> Email sending</CardTitle>
        <p className="text-sm text-muted-foreground">
          Campaigns and year-end statements are delivered through Resend from your own domain.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {connected ? (
          <p className="text-sm" data-testid="email-status-enabled">
            <span className="inline-block px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mr-2">Connected</span>
            Sending as <strong>{status.sender_name} &lt;{status.sender}&gt;</strong> · key {status.key_hint}
          </p>
        ) : (
          <p className="text-sm" data-testid="email-status-preview">
            <span className="inline-block px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-medium mr-2">Preview mode</span>
            Emails are simulated — nothing is actually sent yet.
          </p>
        )}

        <ol className="text-sm text-muted-foreground list-decimal pl-5 space-y-1" data-testid="email-setup-steps">
          <li>Create a free account at <strong>resend.com</strong>.</li>
          <li>In Resend go to <strong>Domains → Add domain</strong>, enter <strong>jbeckministries.org</strong>, and copy the DNS records it shows you.</li>
          <li>Add those records (TXT and CNAME) at the company where your domain is registered — if any record offers a Cloudflare "proxy" toggle, keep it off / DNS only — then click <strong>Verify</strong> in Resend.</li>
          <li>In Resend go to <strong>API Keys → Create API key</strong> (Sending access is enough) and paste it below.</li>
        </ol>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Resend API key {fromSettings ? "(leave blank to keep the current one)" : ""}</Label>
            <Input type="password" placeholder="re_…" value={form.api_key}
              onChange={(e) => setForm({ ...form, api_key: e.target.value })}
              data-testid="email-api-key-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Send from address</Label>
            <Input placeholder="hello@jbeckministries.org" value={form.sender_email}
              onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
              data-testid="email-sender-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Sender name</Label>
            <Input value={form.sender_name}
              onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
              data-testid="email-sender-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Reply-to address (optional)</Label>
            <Input placeholder="office@jbeckministries.org" value={form.reply_to}
              onChange={(e) => setForm({ ...form, reply_to: e.target.value })}
              data-testid="email-reply-to-input" />
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button onClick={save} disabled={busy} data-testid="email-save-btn">Save email settings</Button>
          <Button variant="secondary" onClick={sendTest} disabled={busy || !connected} data-testid="email-test-btn">
            <Send className="h-4 w-4 mr-2" /> Send me a test email
          </Button>
          {fromSettings && (
            <Button variant="ghost" className="text-destructive hover:text-destructive" onClick={remove} disabled={busy} data-testid="email-remove-btn">
              Remove key
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
